import React, { useState, useEffect } from "react";
import "./admin.css";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "./ImageUpload";
import { LayoutDashboard, BookOpen, Users, Calendar, Tag, Image, CalendarDays, Mail, Settings, LogOut, Shirt, FileText, ExternalLink, Menu, X, Megaphone, GraduationCap, CreditCard, ClipboardList, HelpCircle, UserCog, CalendarCheck, CalendarRange, Coins, Sparkles, Send, FileSpreadsheet, Wrench, Scale, ShoppingBag, Store } from "lucide-react";

// Menu grouped by section
const linkSections = [
  {
    label: "Vue d'ensemble",
    links: [
      { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: "Gestion des élèves",
    links: [
      { to: "/admin/level-requests", label: "Demandes de niveau", icon: HelpCircle },
      { to: "/admin/families", label: "Familles", icon: Users },
      { to: "/admin/students", label: "Élèves", icon: GraduationCap },
      { to: "/admin/enrollments", label: "Inscriptions (détail)", icon: ClipboardList },
      { to: "/admin/enrollments-by-slot", label: "Inscriptions par créneau", icon: CalendarRange },
      { to: "/admin/attendance", label: "Présences", icon: CalendarCheck },
    ],
  },
  {
    label: "Commandes & paiements",
    links: [
      { to: "/admin/orders", label: "Commandes", icon: ClipboardList },
      { to: "/admin/payments", label: "Paiements", icon: CreditCard },
      { to: "/admin/sepa-diagnostic", label: "Diagnostic SEPA", icon: Wrench },
    ],
  },
  {
    label: "Planning & inscription",
    links: [
      { to: "/admin/schedule", label: "Planning des créneaux", icon: CalendarRange },
      { to: "/admin/stages", label: "Stages de vacances", icon: Sparkles },
    ],
  },
  {
    label: "Communication",
    links: [
      { to: "/admin/broadcast", label: "Envoi de mails", icon: Send },
      { to: "/admin/contacts", label: "Messages contact", icon: Mail },
      { to: "/admin/inscriptions", label: "Pré-inscriptions (anciennes)", icon: FileText },
    ],
  },
  {
    label: "Boutique",
    links: [
      { to: "/admin/boutique/commandes", label: "Commandes boutique", icon: ShoppingBag },
      { to: "/admin/boutique/produits", label: "Produits", icon: Store },
      { to: "/admin/boutique/codes-promo", label: "Codes de réduction", icon: Tag },
    ],
  },
  {
    label: "Gestion du site",
    links: [
      { to: "/admin/categories", label: "Catégories de cours", icon: Tag },
      { to: "/admin/courses", label: "Cours (présentation)", icon: BookOpen },
      { to: "/admin/home-dances", label: "Accueil — Une danse pour chacun", icon: Sparkles },
      { to: "/admin/teachers", label: "Professeurs", icon: GraduationCap },
      { to: "/admin/board-members", label: "Bureau de l'association", icon: Users },
      { to: "/admin/tarifs", label: "Tarifs (affichage public)", icon: Tag },
      { to: "/admin/tenues", label: "Tenues", icon: Shirt },
      { to: "/admin/dates", label: "Dates importantes", icon: CalendarDays },
      { to: "/admin/infos", label: "Bannières d'accueil", icon: Megaphone },
      { to: "/admin/gallery-categories", label: "Catégories de galerie", icon: Tag },
      { to: "/admin/gallery", label: "Galerie photos", icon: Image },
      { to: "/admin/settings/site", label: "Paramètres du site", icon: Settings },
      { to: "/admin/legal", label: "Pages légales & RGPD", icon: Scale },
    ],
  },
  {
    label: "Gestion",
    links: [
      { to: "/admin/accounting", label: "Comptabilité (export Excel)", icon: FileSpreadsheet },
      { to: "/admin/settings/finance", label: "Paramètres financiers", icon: Coins },
      { to: "/admin/admins", label: "Administrateurs", icon: UserCog },
    ],
  },
];

// Sidebar for a "restricted admin" (admin linked to a teacher).
// Only the 3 authorized BO views are exposed.
const RESTRICTED_SECTIONS = [
  {
    label: "Espace professeur",
    links: [
      { to: "/admin/attendance", label: "Présences", icon: CalendarCheck },
      { to: "/admin/broadcast", label: "Envoi de mails", icon: Send },
      { to: "/admin/enrollments-by-slot", label: "Inscriptions par créneau", icon: CalendarRange },
    ],
  },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { settings } = useData();
  const logo = resolveImageUrl(settings?.logo);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-slate-800 flex items-center justify-between">
        <div>
          {logo ? (
            <img src={logo} alt="L'Élan Danse" className="h-12 lg:h-14 w-auto object-contain bg-white/95 rounded-md p-1.5" />
          ) : (
            <div className="flex flex-col leading-none">
              <span className="font-script text-3xl text-blush">L&apos;élan</span>
              <span className="font-script text-2xl text-blush -mt-1">Danse</span>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-3 font-semibold">
            {user?.restricted ? "Administration · Professeur" : "Administration"}
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="lg:hidden p-2 -mr-2 text-slate-400 hover:text-white"
          aria-label="Fermer le menu"
        >
          <X size={22} />
        </button>
      </div>
      <nav className="flex-1 p-3 overflow-y-auto">
        {(user?.restricted ? RESTRICTED_SECTIONS : linkSections).map((section) => (
          <div key={section.label} className="mb-5">
            <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">{section.label}</div>
            {section.links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors mx-1 my-0.5 ${
                    isActive
                      ? "bg-slate-800 text-white border-l-2 border-blush pl-[10px] font-semibold"
                      : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
              >
                <l.icon size={17} /> {l.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800 space-y-1">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
          <ExternalLink size={16} /> Voir le site
        </a>
        <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
        <button
          onClick={() => { logout(); navigate("/admin/login"); }}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-red-600 hover:text-white transition-colors"
        >
          <LogOut size={16} /> Se déconnecter
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-100 admin-scope">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 text-slate-200"
          aria-label="Ouvrir le menu"
        >
          <Menu size={22} />
        </button>
        {logo ? (
          <img src={logo} alt="L'Élan Danse" className="h-9 w-auto object-contain bg-white/95 rounded p-1" />
        ) : (
          <span className="font-script text-2xl text-blush">L&apos;élan Danse</span>
        )}
        <div className="w-9" />
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 shrink-0 bg-slate-900 border-r border-slate-800 flex-col h-screen sticky top-0">
          {sidebarContent}
        </aside>

        {/* Mobile drawer */}
        {open && (
          <>
            <div
              className="fixed inset-0 z-40 bg-stone-900/60 lg:hidden"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <aside className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-slate-900 border-r border-slate-800 flex flex-col lg:hidden animate-in slide-in-from-left">
              {sidebarContent}
            </aside>
          </>
        )}

        <main className="flex-1 min-w-0 overflow-x-hidden">
          <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
